<template>
  <div class="relative" ref="menuRef">
    <!-- Waffle Menu Button -->
    <button
      @click="toggleMenu"
      class="flex items-center justify-center w-9 h-9 rounded-md hover:bg-gray-100 transition-colors duration-200"
      :class="{ 'bg-gray-100': isMenuOpen }"
      aria-label="App Switcher"
      aria-haspopup="true"
      :aria-expanded="isMenuOpen"
    >
      <div
        v-html="WAFFLE_MENU_ICON"
        class="text-gray-500 hover:text-gray-700"
      ></div>
    </button>

    <!-- Dropdown Menu -->
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isMenuOpen"
        class="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
      >
        <div class="px-3 py-2">
          <p
            class="text-xs font-semibold text-gray-400 uppercase tracking-wider"
          >
            Applications
          </p>
        </div>

        <!-- Experts & Programs Option -->
        <button
          @click="navigateToExpertsPrograms"
          class="w-full px-3 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors duration-150"
          :class="{ 'bg-violet-50': currentApp === 'experts-programs' }"
        >
          <div
            class="flex items-center justify-center w-10 h-10 rounded-lg bg-violet-100"
          >
            <div v-html="EXPERTISE_ICON" class="text-violet-600"></div>
          </div>
          <div class="flex-1 text-left">
            <p
              class="text-sm font-medium text-gray-900"
              :class="{ 'text-violet-600': currentApp === 'experts-programs' }"
            >
              Experts & Programs
            </p>
            <p class="text-xs text-gray-500">Manage your expert network</p>
          </div>
          <div
            v-if="currentApp === 'experts-programs'"
            class="w-2 h-2 rounded-full bg-violet-600"
          ></div>
        </button>

        <!-- Expert Insights Option -->
        <button
          @click="navigateToInsights"
          class="w-full px-3 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors duration-150"
          :class="{ 'bg-violet-50': currentApp === 'insights' }"
        >
          <div
            class="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100"
          >
            <div v-html="INSIGHTS_SEARCH_ICON" class="text-blue-600"></div>
          </div>
          <div class="flex-1 text-left">
            <p
              class="text-sm font-medium text-gray-900 flex items-center gap-2"
              :class="{ 'text-violet-600': currentApp === 'insights' }"
            >
              Expert Insights
            </p>
            <p class="text-xs text-gray-500">
              Search call transcripts & trends
            </p>
          </div>
          <div
            v-if="currentApp === 'insights'"
            class="w-2 h-2 rounded-full bg-violet-600"
          ></div>
        </button>

        <!-- Divider -->
        <div class="my-2 border-t border-gray-100"></div>

        <!-- Help/Info Section -->
        <div class="px-3 py-2">
          <p class="text-xs text-gray-400">
            Switch between apps to access different features
          </p>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useUserStore } from "@/stores/user";
import {
  WAFFLE_MENU_ICON,
  INSIGHTS_SEARCH_ICON,
  LOCK_ICON,
  EXPERTISE_ICON,
} from "@/assets/svg/dashboard/svg-constants";

export default defineComponent({
  name: "AppSwitcherMenu",
  setup() {
    const router = useRouter();
    const route = useRoute();
    const userStore = useUserStore();
    const isMenuOpen = ref(false);
    const menuRef = ref<HTMLElement | null>(null);

    // Determine current app based on route
    const currentApp = computed(() => {
      const path = route.path;
      if (path.startsWith("/insights")) {
        return "insights";
      }
      return "experts-programs";
    });

    const toggleMenu = () => {
      isMenuOpen.value = !isMenuOpen.value;
    };

    const closeMenu = () => {
      isMenuOpen.value = false;
    };

    const navigateToExpertsPrograms = () => {
      closeMenu();
      router.push({ name: "ExpertPrograms" });
    };

    const navigateToInsights = () => {
      closeMenu();
      router.push({ name: "InsightsSearch" });
    };

    // Close menu when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.value && !menuRef.value.contains(event.target as Node)) {
        closeMenu();
      }
    };

    // Close menu on escape key
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };

    onMounted(() => {
      document.addEventListener("click", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
    });

    onUnmounted(() => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    });

    return {
      userStore,
      WAFFLE_MENU_ICON,
      INSIGHTS_SEARCH_ICON,
      LOCK_ICON,
      EXPERTISE_ICON,
      isMenuOpen,
      menuRef,
      currentApp,
      toggleMenu,
      navigateToExpertsPrograms,
      navigateToInsights,
    };
  },
});
</script>
